import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useState } from 'react';
import { api } from '@/api/client';

interface VersionInfo {
  apiVersion: string;
  minClientVersion: string | null;
  latestClientVersion: string | null;
  client: { version: string; updateRequired: boolean; updateAvailable: boolean } | null;
}

/** The web app's VERSION from main.tsx, provided by App. */
export const AppVersionContext = createContext('');
export const useAppVersion = () => useContext(AppVersionContext);

/** Shared (cached) query for GET /version with this client's version. */
export function useVersionInfo(clientVersion: string) {
  return useQuery({
    queryKey: ['version', clientVersion],
    queryFn: () => api.get<VersionInfo>('/version', { clientVersion }),
    enabled: !!clientVersion,
    refetchInterval: 10 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Asks the API whether this build is still supported; blocks on a required update, nudges on an optional one. */
export function VersionCheck({ clientVersion }: { clientVersion: string }) {
  const [dismissed, setDismissed] = useState(false);
  const { data } = useVersionInfo(clientVersion);
  const reload = () => window.location.reload();

  if (data?.client?.updateRequired) {
    return (
      <Dialog open maxWidth="xs" fullWidth>
        <DialogTitle>Update required</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This version of the app ({clientVersion}) is no longer supported. Reload to get the latest version.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={reload}>
            Reload
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Snackbar open={!!data?.client?.updateAvailable && !dismissed} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Alert
        severity="info"
        variant="filled"
        onClose={() => setDismissed(true)}
        action={
          <Button color="inherit" size="small" onClick={reload}>
            Reload
          </Button>
        }
      >
        A new version of the app is available.
      </Alert>
    </Snackbar>
  );
}
